"use client";

import { ArrowLeft, ArrowRight, CreditCard, LogIn, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Alert, Skeleton } from "@nitro/ui";
import { apiFetch } from "../lib/api";
import { money } from "../lib/shop";
import { CartLines, CartMiniSummary, PaymentMethodSelector, ShippingAddressForm, ShippingMethodSelector } from "./ShopCartDrawer";
import { useShopCart } from "./ShopCartProvider";

export function ShopCartPageClient() {
  const {
    cart,
    checkout,
    countryIso2,
    quote,
    removeLine,
    selectedPayment,
    selectedPaymentMethodCode,
    selectedShipping,
    selectedShippingMethodCode,
    setSelectedPaymentMethodCode,
    setSelectedShippingMethodCode,
    shippingAddress,
    setShippingAddress,
    state,
    updateQuantity,
  } = useShopCart();
  const [step, setStep] = useState("cart");
  const [session, setSession] = useState({ loading: true, user: null, error: "" });
  const [contact, setContact] = useState({ name: "", email: "" });
  const steps = useMemo(() => [
    { id: "cart", label: "Carrito" },
    { id: "details", label: quote?.requiresShipping ? "Direccion" : "Resumen" },
    { id: "payment", label: "Pago" },
  ], [quote?.requiresShipping]);

  useEffect(() => {
    if (!cart.length) setStep("cart");
  }, [cart.length]);

  useEffect(() => {
    let active = true;
    apiFetch("/api/v1/auth/me", { cache: "no-store", timeoutMs: 8000 })
      .then((data) => {
        if (!active) return;
        const user = data.user || null;
        setSession({ loading: false, user, error: "" });
        setContact({
          name: user?.displayName || user?.pilot?.displayName || "",
          email: user?.email || "",
        });
      })
      .catch(() => {
        if (active) setSession({ loading: false, user: null, error: "" });
      });
    return () => {
      active = false;
    };
  }, []);

  const currentIndex = Math.max(0, steps.findIndex((item) => item.id === step));
  const canGoDetails = cart.length > 0 && !quote?.errors?.length;
  const canGoPayment = canGoDetails && (!quote?.requiresShipping || Boolean(selectedShippingMethodCode || selectedShipping?.code));
  const canCheckout = Boolean(session.user && contact.email);

  function goNext() {
    if (step === "cart" && canGoDetails) setStep("details");
    if (step === "details" && canGoPayment) setStep("payment");
  }

  function goBack() {
    if (step === "payment") setStep("details");
    if (step === "details") setStep("cart");
  }

  return (
    <section className="nitro-section shop-cart-page">
      <div className="shop-cart-page-head">
        <div>
          <span className="shop-kicker"><ShoppingCart size={15} /> Checkout Nitro</span>
          <h2>Carrito de compra</h2>
          <p>Revisa productos, confirma datos de entrega cuando aplique y elige el metodo de pago disponible.</p>
        </div>
        <a className="nitro-outline-button compact" href="/tienda"><ArrowLeft size={16} /> Seguir comprando</a>
      </div>

      <div className="shop-cart-page-grid">
        <main className="shop-cart-flow">
          <div className="shop-checkout-steps shop-checkout-steps-page" aria-label="Pasos de compra">
            {steps.map((item, index) => (
              <button
                className={step === item.id ? "is-active" : ""}
                disabled={index > currentIndex + 1 || (item.id === "details" && !canGoDetails) || (item.id === "payment" && !canGoPayment)}
                key={item.id}
                onClick={() => setStep(item.id)}
                type="button"
              >
                <span>{index + 1}</span>{item.label}
              </button>
            ))}
          </div>

          {step === "cart" ? (
            <section className="shop-cart-page-panel">
              <div className="shop-cart-panel-head">
                <h3>Productos</h3>
                <span>{cart.length ? `${cart.length} producto(s)` : "Sin productos"}</span>
              </div>
              <CartLines cart={cart} removeLine={removeLine} updateQuantity={updateQuantity} />
            </section>
          ) : null}

          {step === "details" ? (
            <section className="shop-cart-page-panel">
              <div className="shop-cart-panel-head">
                <h3>{quote?.requiresShipping ? "Direccion y envio" : "Resumen de compra"}</h3>
                <span>{quote?.requiresShipping ? "Producto fisico" : "Entrega digital"}</span>
              </div>
              {quote?.requiresShipping ? (
                <div className="shop-cart-page-form-grid">
                  <ShippingAddressForm address={shippingAddress} onChange={setShippingAddress} />
                  <ShippingMethodSelector
                    countryIso2={countryIso2}
                    methods={quote?.shippingMethods || []}
                    selectedCode={selectedShippingMethodCode || selectedShipping?.code || ""}
                    onChange={setSelectedShippingMethodCode}
                  />
                </div>
              ) : (
                <div className="shop-cart-digital-note">
                  <CreditCard size={18} />
                  <div>
                    <strong>Esta compra no requiere direccion.</strong>
                    <p>La entrega se realiza como codigo digital o derecho dentro de Nitro despues de confirmar el pago.</p>
                  </div>
                </div>
              )}
              <CartMiniSummary cart={cart} />
            </section>
          ) : null}

          {step === "payment" ? (
            <section className="shop-cart-page-panel">
              <div className="shop-cart-panel-head">
                <h3>Pago</h3>
                <span>Metodo disponible</span>
              </div>
              <CheckoutContactPanel contact={contact} onChange={setContact} session={session} />
              <PaymentMethodSelector
                countryIso2={countryIso2}
                methods={quote?.paymentMethods || []}
                selectedCode={selectedPaymentMethodCode || selectedPayment?.code || ""}
                onChange={setSelectedPaymentMethodCode}
              />
            </section>
          ) : null}

          <footer className="shop-cart-page-actions">
            {step !== "cart" ? <button className="nitro-outline-button compact" type="button" onClick={goBack}><ArrowLeft size={16} /> Volver</button> : null}
            {step !== "payment" ? (
              <button className="nitro-primary-button" disabled={step === "cart" ? !canGoDetails : !canGoPayment} type="button" onClick={goNext}>
                {step === "cart" ? "Continuar" : "Ir a pago"} <ArrowRight size={16} />
              </button>
            ) : (
              <button className="nitro-primary-button" disabled={!cart.length || !quote?.ok || state.checkingOut || !canCheckout} type="button" onClick={() => checkout({ customer: contact })}>
                {state.checkingOut ? "Creando orden..." : "Pagar ahora"} <ArrowRight size={16} />
              </button>
            )}
          </footer>
        </main>

        <aside className="shop-cart-summary-card">
          <h3>Resumen</h3>
          {state.notice ? <Alert tone="success">{state.notice}</Alert> : null}
          {state.error ? <Alert tone="danger">{state.error}</Alert> : null}
          {quote?.errors?.length ? (
            <Alert tone="danger">
              {quote.errors.map((error) => <span key={error}>{error}</span>)}
            </Alert>
          ) : null}
          {quote?.totals ? (
            <dl className="shop-totals">
              <div><dt>Subtotal</dt><dd>{money(quote.totals.subtotalAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
              <div><dt>Envio</dt><dd>{money(quote.totals.shippingAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
              <div><dt>Total</dt><dd>{money(quote.totals.totalAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
            </dl>
          ) : (
            <p className="shop-muted">Agrega productos para calcular el total.</p>
          )}
          <CartMiniSummary cart={cart} />
        </aside>
      </div>
    </section>
  );
}

function CheckoutContactPanel({ contact, onChange, session }) {
  function updateField(field, value) {
    onChange((current) => ({ ...current, [field]: value }));
  }

  if (session.loading) {
    return (
      <div aria-busy="true" className="shop-contact-card" role="status">
        <span>Revisando sesion...</span>
        <Skeleton height="4.5rem" />
      </div>
    );
  }

  if (!session.user) {
    return (
      <div className="shop-contact-card is-warning">
        <strong>Necesitas iniciar sesion o registrarte para pagar.</strong>
        <p>Usamos tu cuenta para asociar la orden, entregar codigos digitales y mostrar tus compras en Mis ordenes.</p>
        <a className="nitro-primary-button compact" href={`/login?redirect=${encodeURIComponent("/tienda/carrito")}`}><LogIn size={16} /> Iniciar sesion o registrarme</a>
      </div>
    );
  }

  return (
    <div className="shop-contact-card">
      <div>
        <strong>Datos de la orden</strong>
        <p>El recibo y los codigos digitales se enviaran a este correo. Tambien quedaran en tu cuenta Nitro.</p>
      </div>
      <div className="shop-contact-grid">
        <label>Nombre
          <input value={contact.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Nombre para la orden" />
        </label>
        <label>Correo
          <input required type="email" value={contact.email} onChange={(event) => updateField("email", event.target.value)} placeholder="correo@ejemplo.com" />
        </label>
      </div>
    </div>
  );
}
